// serial 플러그인 — AI 콘텐츠 자유 연재 (DEC-95). 누구나 작품을 만들고 회차를 올리고, 로그인 없이 읽는다.
// 서버는 api/internal/serial/rpc.go — Connect-RPC serial.v1.SerialService (proto/serial/v1/serial.proto)
// 행동 이벤트: episode.opened(회차를 엶) · episode.next_opened(다음 회차) · episode.publish(공개 성공) — 가설 카드 지표 (HYPOTHESIS.md)
import { z } from 'zod';

import { definePlugin, rpcTransport } from '../../composition/plugin-runtime';
import { serialApi } from './adapters/serial-api';
import { episodePage } from './presentation/episode-page';
import { feedWidget } from './presentation/feed-widget';
import { newWorkPage } from './presentation/new-work-page';
import { workPage } from './presentation/work-page';
import { writeEpisodePage } from './presentation/write-episode-page';

export default definePlugin({
  name: 'serial',
  version: '0.1.0',
  // 쓰기는 로그인한 사용자만 — 토큰은 auth 플러그인이 채운 세션(ports/session.ts)에서 읽는다
  dependsOn: ['auth'],
  params: z.object({ apiBase: z.string().startsWith('/') }),
  defaults: { params: { apiBase: '/api' } },
  setup(ctx, params) {
    // 작품·회차 Connect-RPC (proto/serial/v1, DEC-101) — 로그인 토큰은 전송이 부르는 시점의 세션에서 싣는다
    const api = serialApi(rpcTransport(params.apiBase));
    ctx.messages('ko', {
      loading: '불러오는 중이에요',
      mine: '내 작품',
      signInToWrite: '오른쪽 위에서 로그인하면 연재할 수 있어요',
      'ai.none': 'AI 사용 안 함',
      'ai.none.hint': '직접 썼어요',
      'ai.assisted': 'AI 보조',
      'ai.assisted.hint': '아이디어·다듬기에 AI를 썼어요',
      'ai.generated': 'AI 생성',
      'ai.generated.hint': '본문을 AI로 만들었어요',
      'feed.label': '연재 중인 작품',
      'feed.write': '작품 쓰기',
      'feed.empty': '아직 연재 중인 작품이 없어요. 첫 작품을 올려 보세요',
      'feed.episodes': '회차',
      'feed.more': '더 보기',
      'work.readFirst': '첫 회차부터 읽기',
      'work.writeEpisode': '회차 쓰기',
      'work.noEpisodes': '아직 올라온 회차가 없어요',
      'episode.toWork': '작품으로',
      'episode.prev': '이전 회차',
      'episode.next': '다음 회차',
      'episode.last': '마지막 회차예요',
      'new.heading': '새 작품',
      'new.title': '제목',
      'new.synopsis': '소개 (선택)',
      'new.aiUsage': 'AI를 얼마나 썼나요? 독자에게 그대로 보여요',
      'new.submit': '만들고 첫 회차 쓰기',
      'write.heading': '회차 쓰기',
      'write.title': '회차 제목 (선택)',
      'write.titlePlaceholder': '비우면 「N화」로 붙어요',
      'write.body': '본문',
      'write.chars': '글자 수(공백 제외)',
      'write.submit': '공개하기',
      'remove.work': '작품 내리기',
      'remove.workQuestion': '이 작품과 회차가 모두 목록에서 사라져요. 내릴까요?',
      'remove.episode': '이 회차 내리기',
      'remove.episodeQuestion': '이 회차가 목록에서 사라져요. 번호는 다시 쓰지 않아요. 내릴까요?',
      'remove.confirm': '내리기',
      'remove.cancel': '취소',
      'error.login_required': '로그인이 풀렸어요. 오른쪽 위에서 다시 로그인해 주세요',
      'error.not_author': '내 작품에만 회차를 올릴 수 있어요',
      'error.not_found': '없거나 내려간 작품·회차예요',
      'error.empty_body': '본문을 써 주세요',
      'error.too_short': '본문이 너무 짧아요. 최소 글자 수',
      'error.daily_limit': '오늘 올릴 수 있는 회차를 다 올렸어요. 하루 상한',
      'error.invalid_field': '입력한 내용을 확인해 주세요',
      'error.too_large': '본문이 너무 길어요',
      'error.network': '연결이 끊겼어요. 잠시 뒤 다시 시도해 주세요',
      'error.failed': '잠시 문제가 생겼어요. 다시 시도해 주세요',
    });
    ctx.messages('en', {
      loading: 'Loading',
      mine: 'Mine',
      signInToWrite: 'Sign in at the top right to publish',
      'ai.none': 'No AI',
      'ai.none.hint': 'Written by me',
      'ai.assisted': 'AI-assisted',
      'ai.assisted.hint': 'Used AI for ideas or editing',
      'ai.generated': 'AI-generated',
      'ai.generated.hint': 'The text was generated with AI',
      'feed.label': 'Ongoing stories',
      'feed.write': 'Write',
      'feed.empty': 'No stories yet. Post the first one',
      'feed.episodes': 'Episodes',
      'feed.more': 'More',
      'work.readFirst': 'Start reading',
      'work.writeEpisode': 'Write an episode',
      'work.noEpisodes': 'No episodes yet',
      'episode.toWork': 'Back to story',
      'episode.prev': 'Previous',
      'episode.next': 'Next episode',
      'episode.last': 'This is the latest episode',
      'new.heading': 'New story',
      'new.title': 'Title',
      'new.synopsis': 'Synopsis (optional)',
      'new.aiUsage': 'How much AI did you use? Readers will see this',
      'new.submit': 'Create and write episode 1',
      'write.heading': 'Write an episode',
      'write.title': 'Episode title (optional)',
      'write.titlePlaceholder': 'Leave empty for "Episode N"',
      'write.body': 'Text',
      'write.chars': 'Characters (no spaces)',
      'write.submit': 'Publish',
      'remove.work': 'Take down story',
      'remove.workQuestion': 'This story and all its episodes will disappear from the list. Take it down?',
      'remove.episode': 'Take down episode',
      'remove.episodeQuestion': 'This episode will disappear. Its number will not be reused. Take it down?',
      'remove.confirm': 'Take down',
      'remove.cancel': 'Cancel',
      'error.login_required': 'You were signed out. Please sign in again at the top right',
      'error.not_author': 'You can only add episodes to your own story',
      'error.not_found': 'This story or episode does not exist or was removed',
      'error.empty_body': 'Please write the text',
      'error.too_short': 'The text is too short. Minimum characters',
      'error.daily_limit': 'You reached today’s episode limit',
      'error.invalid_field': 'Please check what you entered',
      'error.too_large': 'The text is too long',
      'error.network': 'Connection lost. Please try again shortly',
      'error.failed': 'Something went wrong. Please try again',
    });
    ctx.slot('home.main', feedWidget(api), { priority: 50 });
    ctx.route('/write', newWorkPage(api));
    ctx.route('/works/:id', workPage(api));
    ctx.route('/works/:id/write', writeEpisodePage(api));
    ctx.route('/episodes/:id', episodePage(api));
  },
});
